import type { ForqSupabaseClient } from './client';
import {
  normalizeOrderInput,
  normalizeOrderSearchInput,
  type CreateOrderDocumentInput,
  type OrderCustomerOption,
  type OrderDocumentRecord,
  type OrderGateway,
  type OrderItemOption,
  type OrderMachineOption,
  type OrderOverview,
  type OrderPage,
  type OrderRecord,
  type OrderSearchInput,
  type SaveOrderInput,
  type TransitionOrderInput,
} from '@forq/orders-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

function object<T>(operation: string, value: unknown): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError(operation, 'The RPC returned an invalid object.');
  }
  return value as T;
}

function array<T>(operation: string, value: unknown): readonly T[] {
  if (!Array.isArray(value)) throw new SupabaseOperationError(operation, 'The RPC returned an invalid list.');
  return value as readonly T[];
}

export class SupabaseOrdersGateway implements OrderGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async getOverview(tenantId: string): Promise<OrderOverview> {
    const { data, error } = await this.client.rpc('order_overview', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('orders.overview', messageOf(error));
    return object<OrderOverview>('orders.overview', data);
  }

  async searchOrders(input: OrderSearchInput): Promise<OrderPage> {
    const normalized = normalizeOrderSearchInput(input);
    const { data, error } = await this.client.rpc('order_search', {
      p_tenant_id: normalized.tenantId,
      p_query: normalized.query,
      p_bucket: normalized.bucket === 'all' ? null : normalized.bucket,
      p_profile: normalized.profile === 'all' ? null : normalized.profile,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('orders.search', messageOf(error));
    return object<OrderPage>('orders.search', data);
  }

  async getOrder(tenantId: string, orderId: string): Promise<OrderRecord> {
    const { data, error } = await this.client.rpc('order_get', {
      p_tenant_id: tenantId,
      p_order_id: orderId,
    });
    if (error) throw new SupabaseOperationError('orders.get', messageOf(error));
    return object<OrderRecord>('orders.get', data);
  }

  async searchCustomerOptions(tenantId: string, query = ''): Promise<readonly OrderCustomerOption[]> {
    const { data, error } = await this.client.rpc('order_customer_options', {
      p_tenant_id: tenantId,
      p_query: query.trim(),
      p_limit: 50,
    });
    if (error) throw new SupabaseOperationError('orders.customers', messageOf(error));
    return array<OrderCustomerOption>('orders.customers', data ?? []);
  }

  async searchMachineOptions(tenantId: string, query = '', customerId: string | null = null, includeAll = false): Promise<readonly OrderMachineOption[]> {
    const { data, error } = await this.client.rpc('order_machine_options', {
      p_tenant_id: tenantId,
      p_query: query.trim(),
      p_customer_id: customerId || null,
      p_include_all: includeAll,
      p_limit: 50,
    });
    if (error) throw new SupabaseOperationError('orders.machines', messageOf(error));
    return array<OrderMachineOption>('orders.machines', data ?? []);
  }

  async searchItemOptions(tenantId: string, query = ''): Promise<readonly OrderItemOption[]> {
    const { data, error } = await this.client.rpc('order_item_options', {
      p_tenant_id: tenantId,
      p_query: query.trim(),
      p_limit: 50,
    });
    if (error) throw new SupabaseOperationError('orders.items', messageOf(error));
    return array<OrderItemOption>('orders.items', data ?? []);
  }

  async saveOrder(rawInput: SaveOrderInput): Promise<OrderRecord> {
    const input = normalizeOrderInput(rawInput);
    const { data, error } = await this.client.rpc('order_save', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('orders.save', messageOf(error));
    return object<OrderRecord>('orders.save', data);
  }

  async transitionOrder(input: TransitionOrderInput): Promise<OrderRecord> {
    const { data, error } = await this.client.rpc('order_transition', {
      p_tenant_id: input.tenantId,
      p_order_id: input.orderId,
      p_to_status: input.toStatus,
      p_reason: input.reason?.trim() ?? '',
    });
    if (error) throw new SupabaseOperationError('orders.transition', messageOf(error));
    return object<OrderRecord>('orders.transition', data);
  }

  async createDocument(input: CreateOrderDocumentInput): Promise<OrderDocumentRecord> {
    const { data, error } = await this.client.rpc('order_document_create', {
      p_tenant_id: input.tenantId,
      p_order_id: input.orderId,
      p_kind: input.kind,
    });
    if (error) throw new SupabaseOperationError('orders.document.create', messageOf(error));
    return object<OrderDocumentRecord>('orders.document.create', data);
  }

  async listDocuments(tenantId: string, orderId: string): Promise<readonly OrderDocumentRecord[]> {
    const { data, error } = await this.client.rpc('order_document_list', {
      p_tenant_id: tenantId,
      p_order_id: orderId,
    });
    if (error) throw new SupabaseOperationError('orders.document.list', messageOf(error));
    return array<OrderDocumentRecord>('orders.document.list', data ?? []);
  }
}
